import React from "react";
import { type FormatDateOptions, useIntl as reactIntlUseIntl } from "react-intl";

import type { FormattedDateTimeForFileNameProps, Locale, TranslationId } from "@eroad/types";

import { TIMEZONE_USER } from "../constants/constants";

/**
 * React hook that returns translation & date/number format helpers, current locale and timeZone.
 * Must be used within a component that is a descendant of an `IntlProvider` component.
 *
 * @example
 * ```js
 * import { useIntl } from "@eroad/i18n/useIntl";
 *
 * const Component = () => {
 *   const { t, formatNumber, timeZone } = useIntl();
 *
 *   return (
 *     <>
 *       <h4>Title: {t("translation.key.title")}</h4>
 *       <span>Description: {t({ id: "translation.key.description" })}</span>
 *       <span>Currency: {formatNumber(totalCost, { style: "currency", currency: NZD })}</span>
 *       Current timeZone is: {timeZone}
 *     </>
 *   );
 * };
 * ```
 */
const useIntl = () => {
  const intl = reactIntlUseIntl();

  return React.useMemo(
    () => ({
      formatDate: (date: Date | number | string | undefined, options?: FormatDateOptions) =>
        intl.formatDate(date, options).replace(/ at/g, ", ").replace(/PM/g, "pm").replace(/AM/g, "am"),
      formatList: intl.formatList,
      formatNumber: intl.formatNumber,
      formattedDateTimeForFileName: ({ dateTime, timeZone }: FormattedDateTimeForFileNameProps) =>
        intl
          .formatDate(dateTime, {
            day: "numeric",
            hour: "numeric",
            hour12: false,
            minute: "numeric",
            month: "short",
            second: "numeric",
            timeZone,
            timeZoneName: "short",
            year: "numeric",
          })
          .replace(/(:| )/g, "_")
          .replace(/,/g, "")
          .replace(/at_/g, ""),
      locale: intl.locale as unknown as Locale,
      messages: intl.messages,
      onError: intl.onError,
      /**
       * Don't use as a hook dependency - use outcome of this function instead
       * Relying on function reference may cause unnecessary re-renders if function is not memoized
       *
       * @remarks
       * It does not support automatic line breaks in translations, like FormattedMessage does.
       * `values={{ br: <br /> }}` needs always be provided if translation contains `{br}` element.
       */
      t: (translationId: TranslationId) => {
        if (typeof translationId === "string" && translationId !== "") {
          return intl.formatMessage({ id: translationId });
        }

        if (translationId && "id" in translationId) {
          const { defaultMessage, id, values } = translationId;
          if (typeof id === "string" && id !== "") {
            return intl.formatMessage({ defaultMessage, id }, values);
          }
        }

        return "";
      },
      timeZone: intl.timeZone ?? TIMEZONE_USER,
    }),
    [intl],
  );
};

type UseIntlType = ReturnType<typeof useIntl>;

export { useIntl };
export type { UseIntlType };
